import { useEffect, useState } from "react";
import { Upload, Loader2, Trash2, Wand2, ArrowLeft, ArrowRight, FileDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DOC_TYPES, SCAN_MODES, blobUrl } from "@/lib/scanOptions";
import { CornerEditor } from "@/components/scan/CornerEditor";
import { LinkSelect, linkPayload } from "@/components/scan/LinkSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PageThumbs = ({ pages, active, setActive, swap, remove }) => (
  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="scan-page-thumbs">
    {pages.map((p, i) => (
      <div key={p.id} className={`relative shrink-0 w-20 rounded-md border-2 overflow-hidden cursor-pointer ${i === active ? "border-emerald-600" : "border-slate-200"}`} onClick={() => setActive(i)} data-testid={`scan-page-thumb-${i}`}>
        <img src={p.processedUrl || p.originalUrl} alt="" className="w-full h-24 object-cover" />
        <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] flex justify-between px-1">
          <button type="button" data-testid={`scan-page-left-${i}`} onClick={(e) => { e.stopPropagation(); swap(i, i - 1); }}><ArrowLeft className="w-3 h-3" /></button>
          <span>{i + 1}{p.processedUrl ? " ✓" : ""}</span>
          <button type="button" data-testid={`scan-page-right-${i}`} onClick={(e) => { e.stopPropagation(); swap(i, i + 1); }}><ArrowRight className="w-3 h-3" /></button>
        </div>
        <button type="button" data-testid={`scan-page-remove-${i}`} onClick={(e) => { e.stopPropagation(); remove(i); }} className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full p-0.5"><Trash2 className="w-3 h-3" /></button>
      </div>
    ))}
  </div>
);

const PageEditor = ({ page, mode, onPoints, onApply, busy }) => {
  const [view, setView] = useState("edit");
  useEffect(() => setView(page.processedUrl ? "result" : "edit"), [page.id, page.processedUrl]);
  return (
    <div className="space-y-2">
      <div className="flex gap-2 text-xs">
        <button type="button" data-testid="scan-view-edit" className={view === "edit" ? "font-bold underline" : "text-slate-500"} onClick={() => setView("edit")}>Köşeleri düzenle</button>
        {page.processedUrl && <button type="button" data-testid="scan-view-result" className={view === "result" ? "font-bold underline" : "text-slate-500"} onClick={() => setView("result")}>Sonuç</button>}
      </div>
      <div className="max-h-[48vh] overflow-auto rounded-md bg-slate-100 p-2 flex justify-center">
        <div className="w-full max-w-md">
          {view === "edit" ? <CornerEditor src={page.originalUrl} points={page.points} onChange={onPoints} />
            : <img data-testid="scan-result-image" src={page.processedUrl} alt="" className="w-full rounded-md shadow" />}
        </div>
      </div>
      {view === "edit" && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">Yeşil noktalar köşeler, mavi noktalar kenar düzeltme noktalarıdır. Kıvrık sayfalarda mavi noktaları kenara oturtun.</span>
          <Button type="button" size="sm" data-testid="scan-apply-btn" disabled={busy} onClick={onApply}>{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}Kırp ve {SCAN_MODES[mode].split(" ")[0].toLowerCase()} uygula</Button>
        </div>
      )}
    </div>
  );
};

export const ScanWizard = ({ open, onClose, onSaved, preset }) => {
  const [meta, setMeta] = useState({ title: "", doc_type: "TAPU", mode: "SHADOW_FREE" });
  const [link, setLink] = useState({});
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setMeta({ title: "", doc_type: "TAPU", mode: "SHADOW_FREE" }); setLink(preset || {}); setPages([]); setActive(0); }
  }, [open]);

  const patch = (i, p) => setPages((ps) => ps.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const upload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    setBusy(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/scans/pages", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const originalUrl = await blobUrl(`/scans/pages/${data.id}/image`);
        setPages((ps) => { setActive(ps.length); return [...ps, { id: data.id, points: data.points, originalUrl, processedUrl: null }]; });
      } catch (x) { err(x); }
    }
    setBusy(false);
  };

  const apply = async (i = active) => {
    const p = pages[i];
    setBusy(true);
    try {
      await api.post(`/scans/pages/${p.id}/process`, { points: p.points, mode: meta.mode });
      patch(i, { processedUrl: `${await blobUrl(`/scans/pages/${p.id}/image?kind=processed`)}` });
    } catch (x) { err(x); }
    setBusy(false);
  };

  const swap = (i, j) => j >= 0 && j < pages.length && setPages((ps) => { const n = [...ps]; [n[i], n[j]] = [n[j], n[i]]; setActive(j); return n; });
  const remove = (i) => { api.delete(`/scans/pages/${pages[i].id}`).catch(() => {}); setPages((ps) => ps.filter((_, j) => j !== i)); setActive(0); };
  const cancel = () => { pages.forEach((p) => api.delete(`/scans/pages/${p.id}`).catch(() => {})); onClose(); };

  const save = async () => {
    setBusy(true);
    try {
      for (const p of pages.filter((x) => !x.processedUrl)) await api.post(`/scans/pages/${p.id}/process`, { points: p.points, mode: meta.mode });
      await api.post("/scans", { title: meta.title, doc_type: meta.doc_type, page_ids: pages.map((p) => p.id), ...linkPayload(link) });
      toast.success("Belge PDF olarak kaydedildi");
      onSaved();
      onClose();
    } catch (x) { err(x); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && cancel()}>
      <DialogContent className="max-w-3xl max-h-[94vh] overflow-y-auto" data-testid="scan-wizard">
        <DialogHeader><DialogTitle>Belge Tara</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-3 md:col-span-1"><Label>Belge adı *</Label><Input data-testid="scan-title-input" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} placeholder="Örn. Ahmet Bey tapu" /></div>
          <div><Label>Belge türü</Label>
            <Select value={meta.doc_type} onValueChange={(v) => setMeta({ ...meta, doc_type: v })}>
              <SelectTrigger data-testid="scan-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(DOC_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Görünüm</Label>
            <Select value={meta.mode} onValueChange={(v) => { setMeta({ ...meta, mode: v }); setPages((ps) => ps.map((p) => ({ ...p, processedUrl: null }))); }}>
              <SelectTrigger data-testid="scan-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SCAN_MODES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <LinkSelect value={link} onChange={setLink} />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Sayfalar ({pages.length})</Label>
            <label className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 cursor-pointer">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Sayfa ekle / fotoğraf çek
              <input data-testid="scan-page-upload" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload} disabled={busy} />
            </label>
          </div>
          {pages.length === 0 ? <div className="rounded-lg border-2 border-dashed p-10 text-center text-sm text-slate-500">Belgenin fotoğrafını yükleyin; köşeler otomatik bulunur.</div> : (
            <>
              <PageThumbs pages={pages} active={active} setActive={setActive} swap={swap} remove={remove} />
              {pages[active] && <PageEditor page={pages[active]} mode={meta.mode} busy={busy} onApply={() => apply()} onPoints={(pts) => patch(active, { points: pts, processedUrl: null })} />}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" data-testid="scan-cancel-btn" onClick={cancel} disabled={busy}>İptal</Button>
          <Button data-testid="scan-save-btn" disabled={busy || !pages.length || meta.title.trim().length < 2} className="bg-emerald-600 hover:bg-emerald-700" onClick={save}>
            <FileDown className="w-4 h-4 mr-1" />PDF oluştur ve kaydet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
